// RITA-1602 — Zeigt den Text eines Kapitels: Absätze, Unterkapitel als Zwischenüberschriften, Listen, Hinweiskästen, Bilder
// und Tabellen. Alles entsteht als React-Element aus den Bausteinen in model/handbuchInhalt.ts, kein ungeprüftes HTML.
// Tabellen: mit Platz eine echte Tabelle, in der schmalen Leiste je Zeile ein Kasten (`schmal`). Suchwörter sind markiert.
/* eslint-disable @typescript-eslint/no-use-before-define -- Bausteine enthalten Bausteine (Hinweis, Liste, Tabelle) */
import React from 'react';
import { Alert, AlertColor, Box, Link, Typography } from '@mui/material';
import { handbuchAdresse } from '../model/handbuchAdresse';
import { HandbuchBlock, HandbuchZiel, InlineTeil, istTextstueck, Tabelle } from '../model/handbuchInhalt';
import { textMarkieren } from '../model/handbuchSuche';
import HandbuchBild from './HandbuchBild';

const HINWEIS_FARBEN: Record<string, AlertColor> = {
  info: 'info',
  warnung: 'warning',
  erfolg: 'success',
  fehler: 'error',
};

export interface TextKontext {
  /** Handbuch, in dem gelesen wird; ein Verweis dorthin springt an Ort und Stelle. */
  handbuchId: string;
  /** Leiste: Verweise auf ein anderes Handbuch öffnen die Kachel im neuen Tab, die Leiste bleibt beim Handbuch der Seite. */
  nurEinHandbuch: boolean;
  schmal: boolean;
  /** Überschriftenebene der Unterkapitel: Kachel h3, Leiste h4. */
  unterkapitelEbene: 3 | 4;
  woerter: readonly string[];
  onVerweis: (ziel: HandbuchZiel) => void;
}

function markiert(text: string, woerter: readonly string[]): React.ReactNode {
  if (woerter.length === 0) return text;
  return textMarkieren(text, woerter).map((stueck, stelle) =>
    stueck.markiert ? (
      // eslint-disable-next-line react/no-array-index-key -- Reihenfolge ändert sich nur mit dem ganzen Text
      <mark key={stelle} data-testid="handbuch-markierung">
        {stueck.text}
      </mark>
    ) : (
      stueck.text
    ),
  );
}

function Verweis({ ziel, kontext, children }: { ziel: HandbuchZiel; kontext: TextKontext; children: React.ReactNode }) {
  const adresse = handbuchAdresse({ handbuchId: ziel.handbuch, kapitelId: ziel.kapitel }, ziel.abschnitt);
  const neuerTab = kontext.nurEinHandbuch && ziel.handbuch !== kontext.handbuchId;
  return (
    <Link
      href={adresse}
      underline="always"
      data-testid="handbuch-verweis"
      sx={{ color: (theme) => (theme.palette.mode === 'light' ? 'primary.dark' : 'primary.light') }}
      target={neuerTab ? '_blank' : undefined}
      rel={neuerTab ? 'noopener' : undefined}
      onClick={(ereignis: React.MouseEvent<HTMLAnchorElement>) => {
        if (neuerTab || ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey) return;
        ereignis.preventDefault();
        kontext.onVerweis(ziel);
      }}
    >
      {children}
    </Link>
  );
}

function Inline({ teile, kontext }: { teile: readonly InlineTeil[]; kontext: TextKontext }) {
  return (
    <>
      {teile.map((teil, stelle) => {
        if (!istTextstueck(teil)) {
          // eslint-disable-next-line react/no-array-index-key -- feste Reihenfolge aus den Daten
          return <br key={stelle} />;
        }
        let inhalt = markiert(teil.text, kontext.woerter);
        if (teil.code) {
          inhalt = (
            <Box
              component="code"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.92em',
                px: 0.5,
                borderRadius: 0.5,
                bgcolor: 'background.neutral',
              }}
            >
              {inhalt}
            </Box>
          );
        }
        if (teil.fett) inhalt = <strong>{inhalt}</strong>;
        if (teil.kursiv) inhalt = <em>{inhalt}</em>;
        if (teil.ziel) {
          inhalt = (
            <Verweis ziel={teil.ziel} kontext={kontext}>
              {inhalt}
            </Verweis>
          );
        }
        // eslint-disable-next-line react/no-array-index-key -- feste Reihenfolge aus den Daten
        return <React.Fragment key={stelle}>{inhalt}</React.Fragment>;
      })}
    </>
  );
}

function TabelleBreit({ tabelle, kontext }: { tabelle: Tabelle; kontext: TextKontext }) {
  const rand = { border: 1, borderColor: 'divider', p: 1, verticalAlign: 'top', textAlign: 'left' } as const;
  return (
    <Box sx={{ overflowX: 'auto', my: 1.5 }}>
      <Box
        component="table"
        data-testid="handbuch-tabelle"
        sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 14, overflowWrap: 'break-word' }}
      >
        <thead>
          <tr>
            {tabelle.kopf.map((zelle, stelle) => (
              // eslint-disable-next-line react/no-array-index-key -- Spalten sind fest
              <Box component="th" key={stelle} sx={{ ...rand, bgcolor: 'background.neutral', fontWeight: 600 }}>
                <Bloecke bloecke={zelle} kontext={kontext} kompakt />
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {tabelle.zeilen.map((zeile, zeilenStelle) => (
            // eslint-disable-next-line react/no-array-index-key -- Zeilen sind fest
            <tr key={zeilenStelle}>
              {zeile.map((zelle, stelle) => (
                // eslint-disable-next-line react/no-array-index-key -- Spalten sind fest
                <Box component="td" key={stelle} sx={rand}>
                  <Bloecke bloecke={zelle} kontext={kontext} kompakt />
                </Box>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

/** Schmale Leiste: Jede Zeile ist ein Kasten, die erste Spalte der Titel, die übrigen mit ihrer Spaltenüberschrift. */
function TabelleKaesten({ tabelle, kontext }: { tabelle: Tabelle; kontext: TextKontext }) {
  return (
    <Box data-testid="handbuch-tabelle-kaesten" sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 1.5 }}>
      {tabelle.zeilen.map((zeile, zeilenStelle) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key -- Zeilen sind fest
          key={zeilenStelle}
          data-testid="handbuch-tabellenkasten"
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, overflowWrap: 'anywhere' }}
        >
          <Box sx={{ fontWeight: 600, fontSize: 14 }}>
            <Bloecke bloecke={zeile[0]} kontext={kontext} kompakt />
          </Box>
          {zeile.slice(1).map((zelle, stelle) => (
            // eslint-disable-next-line react/no-array-index-key -- Spalten sind fest
            <Box key={stelle} sx={{ mt: 0.75 }}>
              <Typography component="div" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                <Bloecke bloecke={tabelle.kopf[stelle + 1]} kontext={kontext} kompakt />
              </Typography>
              <Box sx={{ fontSize: 14 }}>
                <Bloecke bloecke={zelle} kontext={kontext} kompakt />
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

function Baustein({ block, kontext, kompakt }: { block: HandbuchBlock; kontext: TextKontext; kompakt?: boolean }) {
  switch (block.typ) {
    case 'absatz':
      return (
        <Typography
          component="p"
          variant={kontext.schmal || kompakt ? 'body2' : 'body1'}
          sx={{
            m: 0,
            mb: kompakt ? 0.25 : 1,
            fontSize: kompakt ? 'inherit' : undefined,
            fontWeight: kompakt ? 'inherit' : undefined,
          }}
        >
          <Inline teile={block.inhalt} kontext={kontext} />
        </Typography>
      );
    case 'ueberschrift': {
      const ebene = kontext.unterkapitelEbene + Math.max(0, block.ebene - 3);
      return (
        <Typography
          component={`h${Math.min(ebene, 6)}` as 'h3'}
          variant="subtitle2"
          data-abschnitt={block.id}
          sx={{ fontWeight: 700, mt: 2, mb: 0.75, scrollMarginTop: 96, fontSize: block.ebene > 3 ? 14 : 15 }}
        >
          {markiert(block.titel, kontext.woerter)}
        </Typography>
      );
    }
    case 'liste':
      return (
        <Box
          component={block.art === 'nummeriert' ? 'ol' : 'ul'}
          start={block.start}
          sx={{ m: 0, mb: 1, pl: 3, fontSize: kontext.schmal ? 14 : 16 }}
        >
          {block.punkte.map((punkt, stelle) => (
            // eslint-disable-next-line react/no-array-index-key -- feste Reihenfolge aus den Daten
            <li key={stelle}>
              <Bloecke bloecke={punkt} kontext={kontext} kompakt={kompakt} />
            </li>
          ))}
        </Box>
      );
    case 'hinweis':
      return (
        <Alert
          severity={HINWEIS_FARBEN[block.art]}
          role="note"
          data-testid="handbuch-hinweis"
          sx={{
            my: 1.25,
            '& .MuiAlert-message': { minWidth: 0, width: '100%' },
            // Im farbigen Kasten stehen Verweise in der Schriftfarbe des Kastens; erkennbar bleiben sie am Unterstrich.
            '& a': { color: 'inherit' },
          }}
        >
          <Bloecke bloecke={block.bloecke} kontext={kontext} />
        </Alert>
      );
    case 'tabelle':
      return kontext.schmal ? (
        <TabelleKaesten tabelle={block} kontext={kontext} />
      ) : (
        <TabelleBreit tabelle={block} kontext={kontext} />
      );
    case 'bild':
      return (
        <HandbuchBild
          bild={block}
          unterschrift={block.unterschrift ? <Inline teile={block.unterschrift} kontext={kontext} /> : null}
        />
      );
    default:
      return null;
  }
}

function Bloecke({
  bloecke,
  kontext,
  kompakt,
}: {
  bloecke: readonly HandbuchBlock[];
  kontext: TextKontext;
  kompakt?: boolean;
}) {
  return (
    <>
      {bloecke.map((block, stelle) => (
        // eslint-disable-next-line react/no-array-index-key -- feste Reihenfolge aus den Daten
        <Baustein key={stelle} block={block} kontext={kontext} kompakt={kompakt} />
      ))}
    </>
  );
}

interface Props {
  bloecke: readonly HandbuchBlock[];
  kontext: TextKontext;
}

/** Jeder oberste Baustein trägt seine Stelle: Ziel für den Sprung aus der Suche. */
export default function HandbuchText({ bloecke, kontext }: Props) {
  return (
    <Box
      data-testid="handbuch-text"
      sx={{
        minWidth: 0,
        overflowWrap: kontext.schmal ? 'anywhere' : 'break-word',
        '& mark': { borderRadius: 0.25, bgcolor: 'warning.light', color: 'common.black' },
      }}
    >
      {bloecke.map((block, stelle) => (
        // eslint-disable-next-line react/no-array-index-key -- feste Reihenfolge aus den Daten
        <Box key={stelle} data-handbuch-block={stelle} sx={{ scrollMarginTop: 96 }}>
          <Baustein block={block} kontext={kontext} />
        </Box>
      ))}
    </Box>
  );
}
